import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent } from '@theme/events';
import { DialogComponent } from '@theme/dialog';
import { fetchConfig, isMobileBreakpoint } from '@theme/utilities';

/** @type {Set<string> | null} */
let injectedStyleSignatures = null;

/**
 * Clones <link rel="stylesheet"> and inline <style> blocks from a fetched
 * product page into the parent document head, so snippet stylesheets apply
 * to morphed modal content. Deduped per session by href / content hash.
 * @param {Document} html
 */
function injectFetchedStyles(html) {
  if (!injectedStyleSignatures) {
    injectedStyleSignatures = new Set();
    document.head.querySelectorAll('link[rel="stylesheet"][href]').forEach((l) => {
      const h = l.getAttribute('href');
      if (h) injectedStyleSignatures?.add(`href:${h}`);
    });
  }
  const seen = injectedStyleSignatures;

  html.querySelectorAll('link[rel="stylesheet"][href]').forEach((linkEl) => {
    const href = linkEl.getAttribute('href');
    if (!href) return;
    const lower = href.toLowerCase();
    if (
      lower.includes('header') ||
      lower.includes('cart-icon') ||
      lower.includes('cart-drawer') ||
      lower.includes('cart-bubble') ||
      lower.includes('base.css') ||
      lower.includes('theme.css') ||
      lower.includes('global.css')
    ) {
      return;
    }
    const sig = `href:${href}`;
    if (seen.has(sig)) return;
    seen.add(sig);
    const clone = document.createElement('link');
    clone.rel = 'stylesheet';
    clone.href = href;
    const media = linkEl.getAttribute('media');
    if (media) clone.media = media;
    clone.setAttribute('data-paired-add-injected', '');
    document.head.appendChild(clone);
  });

  html.querySelectorAll('style').forEach((styleEl) => {
    const text = styleEl.textContent || '';
    if (!text.trim()) return;
    const sig = `style:${text.length}:${text.slice(0, 64)}:${text.slice(-64)}`;
    if (seen.has(sig)) return;
    seen.add(sig);
    const clone = document.createElement('style');
    clone.setAttribute('data-paired-add-injected', '');
    clone.textContent = text;
    document.head.appendChild(clone);
  });
}

/**
 * @param {Element} gridElement
 * @param {string} productTitleText
 * @param {string} productHref
 */
function stripGridForQuickAddMobile(gridElement, productTitleText, productHref) {
  if (!isMobileBreakpoint()) return gridElement;

  const productDetails = gridElement.querySelector('.product-details');
  const productFormComponent = gridElement.querySelector('product-form-component');
  const variantPicker = gridElement.querySelector('variant-picker');
  const productPrice = gridElement.querySelector('product-price');
  const productTitle = document.createElement('a');
  productTitle.textContent = productTitleText || '';
  productTitle.href = productHref || '#';

  const productHeader = document.createElement('div');
  productHeader.classList.add('product-header');
  productHeader.appendChild(productTitle);
  if (productPrice) {
    productHeader.appendChild(productPrice);
  }
  gridElement.appendChild(productHeader);

  if (variantPicker) {
    gridElement.appendChild(variantPicker);
  }
  if (productFormComponent) {
    gridElement.appendChild(productFormComponent);
  }

  productDetails?.remove();
  return gridElement;
}

/**
 * @extends {DialogComponent}
 */
class PairedAddBundleDialog extends DialogComponent {
  /** @type {AbortController | null} */
  #fetchAbort = null;

  /** @type {AbortController} */
  #listenersAbort = new AbortController();

  requiredRefs = ['dialog', 'closeButton', 'baseSlot', 'pairedSlot', 'submitButton', 'errorText'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal: this.#listenersAbort.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#syncSubmitState, { signal: this.#listenersAbort.signal });
    this.refs.submitButton?.addEventListener('click', this.#onSubmit, { signal: this.#listenersAbort.signal });
  }

  disconnectedCallback() {
    this.#listenersAbort.abort();
    this.#listenersAbort = new AbortController();
    this.#fetchAbort?.abort();
    super.disconnectedCallback();
  }

  #onCartUpdate = (/** @type {CustomEvent} */ event) => {
    if (event.detail?.data?.didError) return;
    this.closeDialog();
  };

  /**
   * @param {HTMLElement} trigger
   */
  openFromTrigger = async (trigger) => {
    const direct = trigger.dataset.bundleDirect === 'true';
    if (direct) {
      const baseId = trigger.dataset.baseVariantId;
      const pairedId = trigger.dataset.pairedVariantId;
      if (baseId && pairedId) {
        await this.#postItems(
          [
            { id: Number(baseId), quantity: 1 },
            { id: Number(pairedId), quantity: 1 },
          ],
          trigger
        );
      }
      return;
    }

    const baseUrl = trigger.dataset.baseProductUrl;
    const pairedUrl = trigger.dataset.pairedProductUrl;
    if (!baseUrl || !pairedUrl) return;

    this.#clearError();
    this.#setTriggerLoading(trigger, true);
    this.#fetchAbort?.abort();
    this.#fetchAbort = new AbortController();
    const { signal } = this.#fetchAbort;

    try {
      const [baseDoc, pairedDoc] = await Promise.all([
        this.#fetchProductDoc(baseUrl, signal),
        this.#fetchProductDoc(pairedUrl, signal),
      ]);

      const baseGrid = baseDoc?.querySelector('[data-product-grid-content]');
      const pairedGrid = pairedDoc?.querySelector('[data-product-grid-content]');
      if (!baseGrid || !pairedGrid) {
        this.showDialog();
        this.#showError('Unable to load product options.');
        this.#syncSubmitState();
        return;
      }

      const baseClone = /** @type {Element} */ (baseGrid.cloneNode(true));
      const pairedClone = /** @type {Element} */ (pairedGrid.cloneNode(true));

      stripGridForQuickAddMobile(baseClone, trigger.dataset.baseTitle || '', baseUrl);
      stripGridForQuickAddMobile(pairedClone, trigger.dataset.pairedTitle || '', pairedUrl);

      morph(this.refs.baseSlot, baseClone);
      morph(this.refs.pairedSlot, pairedClone);

      this.#syncSubmitState();
      this.showDialog();
    } catch (e) {
      if (e.name === 'AbortError') return;
      this.showDialog();
      this.#showError('Something went wrong. Please try again.');
      this.#syncSubmitState();
    } finally {
      this.#setTriggerLoading(trigger, false);
    }
  };

  /**
   * @param {HTMLElement} trigger
   * @param {boolean} loading
   */
  #setTriggerLoading(trigger, loading) {
    trigger.classList.toggle('paired-bundle-trigger--loading', loading);
    trigger.toggleAttribute('aria-busy', loading);
    if (trigger instanceof HTMLButtonElement) {
      trigger.disabled = loading;
    }
  }

  /**
   * @param {string} url
   * @param {AbortSignal} signal
   */
  async #fetchProductDoc(url, signal) {
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(String(response.status));
    const text = await response.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    injectFetchedStyles(doc);
    return doc;
  }

  #syncSubmitState = () => {
    const btn = this.refs.submitButton;
    if (!btn) return;

    const v1 = this.#getVariantInput(this.refs.baseSlot);
    const v2 = this.#getVariantInput(this.refs.pairedSlot);

    const ok =
      v1 &&
      v2 &&
      !v1.disabled &&
      !v2.disabled &&
      v1.value &&
      v2.value &&
      v1.value !== '' &&
      v2.value !== '';

    btn.disabled = !ok;
  };

  /**
   * @param {HTMLElement | undefined} root
   */
  #getVariantInput(root) {
    if (!root) return null;
    const form = root.querySelector('product-form-component');
    const input = form?.querySelector?.('input[name="id"]');
    return input instanceof HTMLInputElement ? input : null;
  }

  #onSubmit = async () => {
    const v1 = this.#getVariantInput(this.refs.baseSlot);
    const v2 = this.#getVariantInput(this.refs.pairedSlot);
    if (!v1?.value || !v2?.value || v1.disabled || v2.disabled) return;

    await this.#postItems(
      [
        { id: Number(v1.value), quantity: 1 },
        { id: Number(v2.value), quantity: 1 },
      ],
      this
    );
  };

  /**
   * @param {{ id: number, quantity: number }[]} items
   * @param {EventTarget} sourceEl
   */
  async #postItems(items, sourceEl) {
    this.#clearError();

    const submitBtn = this.refs.submitButton;
    const fromDialogSubmit = sourceEl === this;
    const trigger = sourceEl instanceof HTMLElement && sourceEl.matches('[data-paired-bundle-open]') ? sourceEl : null;

    if (fromDialogSubmit && submitBtn) {
      submitBtn.classList.add('paired-add-bundle-modal__submit--loading');
      submitBtn.disabled = true;
    }
    if (trigger) {
      this.#setTriggerLoading(trigger, true);
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    const payload = {
      items,
      sections: sectionIds.join(','),
    };

    try {
      const fetchCfg = fetchConfig('json', { body: JSON.stringify(payload) });
      const response = await fetch(Theme.routes.cart_add_url, fetchCfg);

      const data = await response.json();

      if (data.status) {
        this.#showError(data.message || 'Could not add to cart');
        document.dispatchEvent(
          new CartAddEvent({}, this.id || 'paired-bundle', {
            didError: true,
            source: 'paired-add-bundle',
          })
        );
        return;
      }

      document.dispatchEvent(
        new CartAddEvent(data, this.id || 'paired-bundle', {
          source: 'paired-add-bundle',
          itemCount: items.reduce((s, i) => s + i.quantity, 0),
          sections: data.sections,
        })
      );
    } catch {
      this.#showError('Network error. Try again.');
    } finally {
      submitBtn?.classList.remove('paired-add-bundle-modal__submit--loading');
      if (trigger) {
        this.#setTriggerLoading(trigger, false);
      }
      if (fromDialogSubmit) {
        this.#syncSubmitState();
      }
    }
  }

  #showError(msg) {
    const el = this.refs.errorText;
    if (!el) return;
    el.textContent = msg;
    el.classList.remove('hidden');
  }

  #clearError() {
    const el = this.refs.errorText;
    if (!el) return;
    el.textContent = '';
    el.classList.add('hidden');
  }
}

if (!customElements.get('paired-add-bundle-dialog')) {
  customElements.define('paired-add-bundle-dialog', PairedAddBundleDialog);
}

document.addEventListener(
  'click',
  (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    const trigger = t.closest('[data-paired-bundle-open]');
    if (!(trigger instanceof HTMLElement)) return;

    const id = trigger.dataset.dialogTarget;
    if (!id) return;

    e.preventDefault();
    const dialog = document.getElementById(id);
    if (dialog instanceof PairedAddBundleDialog) {
      dialog.openFromTrigger(trigger);
    }
  },
  { capture: true }
);
